import type { ThemeConfig, ThemeId } from '@/types/theme';

/**
 * Theme configurations
 * Each theme defines all CSS variable values for consistent theming
 */

export const darkTheme: ThemeConfig = {
  id: 'dark',
  name: 'Dark',
  description: 'Default dark theme with amber accents',
  isDark: true,
  colors: {
    // Base colors
    bg: '#0f0f0f',
    bgSecondary: '#171717',
    card: '#1a1a1a',
    cardHover: '#222222',
    border: '#2a2a2a',
    borderHover: '#3a3a3a',
    text: '#e5e5e5',
    textSecondary: '#d4d4d4',
    muted: '#a0a0a0',

    // Accent colors
    accentPrimary: '#f59e0b',
    accentPrimaryHover: '#fbbf24',
    accentSecondary: '#3b82f6',
    accentSecondaryHover: '#60a5fa',

    // Gradients
    gradientPrimary: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
    gradientSecondary: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',

    // Glass effects
    glassBg: 'rgba(26, 26, 26, 0.85)',
    glassBlur: '12px',

    // Shadows
    shadowGlow: '0 0 20px rgba(245, 158, 11, 0.4)',

    // Scrollbar
    scrollbarTrack: '#1a1a1a',
    scrollbarThumb: '#3a3a3a',
    scrollbarThumbHover: '#4a4a4a',
  },
};

export const lightTheme: ThemeConfig = {
  id: 'light',
  name: 'Light',
  description: 'Clean light theme with warm accents',
  isDark: false,
  colors: {
    // Base colors
    bg: '#ffffff',
    bgSecondary: '#f9fafb',
    card: '#ffffff',
    cardHover: '#f3f4f6',
    border: '#e5e7eb',
    borderHover: '#d1d5db',
    text: '#111827',
    textSecondary: '#374151',
    muted: '#6b7280',

    // Accent colors
    accentPrimary: '#d97706',
    accentPrimaryHover: '#b45309',
    accentSecondary: '#2563eb',
    accentSecondaryHover: '#1d4ed8',

    // Gradients
    gradientPrimary: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
    gradientSecondary: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',

    // Glass effects
    glassBg: 'rgba(255, 255, 255, 0.8)',
    glassBlur: '12px',

    // Shadows
    shadowGlow: '0 0 20px rgba(217, 119, 6, 0.3)',

    // Scrollbar
    scrollbarTrack: '#f3f4f6',
    scrollbarThumb: '#d1d5db',
    scrollbarThumbHover: '#9ca3af',
  },
};

/**
 * All available themes
 * Add new themes here to make them available in the theme picker
 */
export const themes: ThemeConfig[] = [darkTheme, lightTheme];

/**
 * Get theme config by ID
 */
export function getThemeById(id: ThemeId): ThemeConfig {
  return themes.find((t) => t.id === id) ?? darkTheme;
}

/**
 * Valid theme IDs for validation
 */
export const VALID_THEME_IDS: ThemeId[] = themes.map((t) => t.id);

/**
 * Check if a string is a valid theme ID
 */
export function isValidThemeId(id: string): id is ThemeId {
  return VALID_THEME_IDS.includes(id as ThemeId);
}
